import type {
  BillingCheckoutConfig,
  PaddlePricePreviewItem,
  PaddlePricePreviewResponse,
} from "../types/billing";

const PADDLE_SCRIPT_SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";

let paddleScriptPromise: Promise<void> | null = null;
let initializedInstanceKey: string | null = null;

export const loadPaddleScript = async (): Promise<void> => {
  if (typeof window === "undefined") {
    throw new Error("Paddle is only available in the browser.");
  }

  if (window.Paddle) {
    return;
  }

  if (paddleScriptPromise) {
    return paddleScriptPromise;
  }

  paddleScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-paddle-script="true"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Unable to load Paddle.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PADDLE_SCRIPT_SRC;
    script.async = true;
    script.dataset.paddleScript = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load Paddle."));

    document.head.appendChild(script);
  });

  return paddleScriptPromise;
};

export const initializePaddle = async (
  clientToken: string,
  environment: BillingCheckoutConfig["environment"]
): Promise<void> => {
  if (!clientToken) {
    throw new Error("Paddle client token is not configured.");
  }

  await loadPaddleScript();

  if (!window.Paddle) {
    throw new Error("Paddle failed to initialize.");
  }

  const instanceKey = `${environment}:${clientToken}`;

  if (initializedInstanceKey === instanceKey) {
    return;
  }

  if (environment === "sandbox") {
    window.Paddle.Environment?.set("sandbox");
  }

  window.Paddle.Initialize({
    token: clientToken,
  });

  initializedInstanceKey = instanceKey;
};

export const previewPaddlePrices = async (
  items: PaddlePricePreviewItem[]
): Promise<PaddlePricePreviewResponse> => {
  if (!window.Paddle?.PricePreview) {
    throw new Error("Paddle price preview is unavailable.");
  }

  return window.Paddle.PricePreview({ items });
};
